'use client'

import { createContext, useCallback, useContext, useRef } from 'react'

type InteractionLockState = {
  tryAcquireLock: (id: string) => boolean
  releaseLock: (id: string) => void
} | null

const InteractionLockContext = createContext<InteractionLockState>(null)

/**
 * Provides a global locking mechanism to ensure mutually exclusive interactions across components.
 * Useful for ensuring only one card can be hovered, dragged, or activated via touch at a time.
 */
export function InteractionLockProvider({ children }: { children: React.ReactNode }) {
  const activeIdRef = useRef<string | null>(null)

  const tryAcquireLock = useCallback((id: string) => {
    if (activeIdRef.current && activeIdRef.current !== id) {
      return false
    }
    activeIdRef.current = id
    return true
  }, [])

  const releaseLock = useCallback((id: string) => {
    if (activeIdRef.current === id) {
      activeIdRef.current = null
    }
  }, [])

  return (
    <InteractionLockContext.Provider value={{ tryAcquireLock, releaseLock }}>
      {children}
    </InteractionLockContext.Provider>
  )
}

export function useInteractionLock(uniqueId: string) {
  const context = useContext(InteractionLockContext)

  // Fallback if used outside of the provider
  if (!context) {
    return {
      tryAcquireLock: () => true,
      releaseLock: () => {},
    }
  }

  return {
    tryAcquireLock: () => context.tryAcquireLock(uniqueId),
    releaseLock: () => context.releaseLock(uniqueId),
  }
}
